import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { UserWorkspace } from '@/api/types'

/**
 * The workspaces the signed-in user belongs to.
 *
 * Loaded once and then kept current by the realtime layer, which patches this
 * exact key, so the default refetching is turned off: a websocket that is
 * already delivering every change makes a poll on focus pure noise. When the
 * socket cannot say what changed -- `all_notifications_cleared`, say -- the
 * handler invalidates this key explicitly.
 */
export function workspacesQuery() {
  return queryOptions({
    queryKey: qk.workspaces(),
    staleTime: Infinity,
    queryFn: (): Promise<UserWorkspace[]> => unwrap(getApi().GET('/api/workspaces/')),
  })
}

export function useWorkspaces() {
  return useQuery(workspacesQuery())
}

/**
 * Reads a workspace from the loaded list rather than fetching it.
 *
 * There is no endpoint that returns one workspace in this shape -- `users`,
 * `permissions` and the unread count only come from the list -- so a route that
 * wants "the workspace with this id" wants a lookup, not another request.
 */
export function useWorkspace(workspaceId: number | undefined) {
  return useQuery({
    ...workspacesQuery(),
    select: (workspaces) => workspaces.find((w) => w.id === workspaceId),
  })
}

/**
 * Applies a change to the cached list and returns what was there before.
 *
 * The member mutations roll back by restoring that snapshot wholesale rather than
 * by inverting their own change. Inverting is wrong under concurrency: a realtime
 * event can land between the optimistic write and the failure, and the inverse
 * would then undo the event as well as the edit.
 */
export function optimisticallyPatchWorkspaces(
  queryClient: QueryClient,
  update: (workspaces: UserWorkspace[]) => UserWorkspace[],
): UserWorkspace[] | undefined {
  const previous = queryClient.getQueryData<UserWorkspace[]>(qk.workspaces())
  if (previous !== undefined) {
    queryClient.setQueryData<UserWorkspace[]>(qk.workspaces(), update(previous))
  }
  return previous
}

export function rollbackWorkspaces(
  queryClient: QueryClient,
  previous: UserWorkspace[] | undefined,
): void {
  if (previous !== undefined) queryClient.setQueryData(qk.workspaces(), previous)
}
